import React from "react";
import { Table, Tag, Button, Tooltip, Space } from "antd";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { mockTransactions } from "../mockData";

dayjs.extend(utc);

const FlightListView = ({ flights, onShowDetails }) => {
    // 获取特定航班的事务状态
    const getFlightTransactionStatus = (flightId) => {
        const transactions = mockTransactions[flightId] || [];
        // 收集不同类型的事务的最新状态
        const statusMap = {};

        transactions.forEach((tx) => {
            // 只考虑主要事务类型: FUEL, PERMIT, ACTIVATION, CREW, CATERING
            if (["FUEL", "PERMIT", "ACTIVATION", "CREW", "CATERING"].includes(tx.record_type)) {
                // 如果当前事务比已有的更新，或者还没有该类型的记录，则更新状态
                if (!statusMap[tx.record_type] || new Date(tx.created_at) > new Date(statusMap[tx.record_type].created_at)) {
                    statusMap[tx.record_type] = tx;
                }
            }
        });

        return statusMap;
    };

    // 获取状态标签颜色
    const getStatusTagColor = (status) => {
        switch (status) {
            case "COMPLETED":
            case "CONFIRMED":
            case "INFO":
            case "RECORDED":
                return "green";
            case "PENDING":
            case "DRAFT":
                return "blue";
            case "PROCESSING":
                return "purple";
            case "CANCELLED":
                return "red";
            default:
                return "default";
        }
    };

    // 航班状态标签颜色
    const getFlightStatusColor = (status) => {
        switch (status) {
            case "COMPLETED":
                return "green";
            case "DELAYED":
                return "orange";
            case "CANCELLED":
                return "red";
            case "ACTIVE":
                return "purple";
            default:
                return "blue";
        }
    };

    // 渲染事务状态标签
    const renderTransactionTag = (record_type, status) => {
        // 事务类型的简写
        const typeAbbr = {
            FUEL: "FUEL",
            PERMIT: "PMT",
            ACTIVATION: "ACT",
            CREW: "CREW",
            CATERING: "CTR",
        };

        return (
            <Tooltip title={`${record_type}: ${status}`}>
                <Tag color={getStatusTagColor(status)} style={{ margin: "2px" }}>
                    {typeAbbr[record_type]}
                </Tag>
            </Tooltip>
        );
    };

    // 渲染所有事务状态
    const renderTransactionStatus = (flightId) => {
        const statusMap = getFlightTransactionStatus(flightId);

        return (
            <div style={{ display: "flex", flexWrap: "wrap", gap: "2px" }}>
                {Object.entries(statusMap).map(([type, tx]) => renderTransactionTag(type, tx.status))}
                {Object.keys(statusMap).length === 0 && <Tag color="default">无事务</Tag>}
            </div>
        );
    };

    const columns = [
        {
            title: "FLT", // Flight Number
            dataIndex: "flight_number",
            key: "flight_number",
            fixed: "left",
            width: 90,
        },
        {
            title: "DATE",
            dataIndex: "flight_date",
            key: "flight_date",
            render: (text) => dayjs.utc(text).format("DDMMMYY").toUpperCase(),
            width: 100,
        },
        {
            title: "DEP", // Departure
            dataIndex: "departure_airport",
            key: "departure_airport",
            width: 70,
        },
        {
            title: "ARR", // Arrival
            dataIndex: "arrival_airport",
            key: "arrival_airport",
            width: 70,
        },
        {
            title: "STD/STA", // Scheduled Times
            key: "scheduled_times",
            width: 120,
            render: (_, record) => (
                <span>
                    {dayjs.utc(record.std).format("HH:mm")}/{dayjs.utc(record.sta).format("HH:mm")}
                </span>
            ),
        },
        {
            title: "ETD/ETA", // Estimated Times
            key: "estimated_times",
            width: 120,
            render: (_, record) => (
                <span>
                    {record.etd ? dayjs.utc(record.etd).format("HH:mm") : "--"}/{record.eta ? dayjs.utc(record.eta).format("HH:mm") : "--"}
                </span>
            ),
        },
        {
            title: "ATD/ATA", // Actual Times
            key: "actual_times",
            width: 120,
            render: (_, record) => (
                <span>
                    {record.atd ? dayjs.utc(record.atd).format("HH:mm") : "--"}/{record.ata ? dayjs.utc(record.ata).format("HH:mm") : "--"}
                </span>
            ),
        },
        {
            title: "REG", // Registration
            dataIndex: "aircraft_reg",
            key: "aircraft_reg",
            width: 100,
        },
        {
            title: "STATUS", // Flight Status
            dataIndex: "status",
            key: "status",
            width: 100,
            render: (status) => <Tag color={getFlightStatusColor(status)}>{status}</Tag>,
        },
        {
            title: "事务状态", // Transaction Status
            key: "transaction_status",
            width: 180,
            render: (_, record) => renderTransactionStatus(record.id),
        },
        {
            title: "操作",
            key: "action",
            fixed: "right",
            width: 100,
            render: (_, record) => (
                <Button type="link" onClick={() => onShowDetails(record)}>
                    处理事务
                </Button>
            ),
        },
    ];

    return (
        <Table
            columns={columns}
            dataSource={flights}
            rowKey="id"
            scroll={{ x: 1200 }}
            size="small"
            pagination={{
                defaultpageSize: 30,
                showSizeChanger: true,
                pageSizeOptions: ["10", "20", "50"],
            }}
        />
    );
};

export default FlightListView;
